import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { Box, PanelRow, Tooltip } from '@getro/rombo';
import { Flex, Image, Text, Link } from 'rebass/styled-components';
import moment from 'moment';
import BlankMessage from 'components/molecules/BlankMessage';
import SearchIcon from '../../../assets/icon-effortless-search.svg';
import ForbiddenIcon from '../../../assets/icon-forbidden.svg';
import JobSource from '../../../components/molecules/jobSource';
import NetworkSchema from '../../../schemas/network';
import UserSchema from '../../../schemas/user';
import OrganizationSchema from '../../../schemas/organization';

const CareersPages = ({ company, activeNetwork, currentUser }) => {
  const { slug } = activeNetwork;
  const { domain } = company;
  const { firstName, lastName, email } = currentUser;
  const {
    id: companyId,
    name: companyName,
    websiteUrl: companyUrl,
    organizationCareersPages: ocps,
    jobSourceCustomerFacingStatus,
    slug: companySlug,
    activeJobsCount,
    lastSuccessfullyScrapedAt,
  } = company;

  const jobSourceChangeUrl = useMemo(() => {
    const qp = new URLSearchParams({
      firstname: firstName || '',
      companyname: companyName || '',
      email: email || '',
      lastname: lastName || '',
      companyid: String(companyId ?? ''),
      companyurl: companyUrl || '',
    }).toString();
    return `https://getro-forms.typeform.com/to/yMmzpUKy#${qp}`;
  }, [companyId, companyName, companyUrl, email, firstName, lastName]);

  const hasNoDomain = !domain;

  const renderContent = () => {
    if (hasNoDomain) {
      return (
        <Flex flexDirection={['column']} alignItems="center" textAlign="center" pb="12px">
          <Image src={ForbiddenIcon} mb={3} width="64px" height="64px" />
          <BlankMessage
            title="Extracted jobs are not available"
            content="Because this company has no website, we are not able to add a job source. You can always post jobs below."
            pt="4px"
          />
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'Off') {
      return (
        <Flex flexDirection={['column']} alignItems="center" textAlign="center">
          <Image src={ForbiddenIcon} width="64px" pb="4px" />
          <BlankMessage title="Automated job extraction is switched off for this company" pt="4px">
            <Text>
              To turn it back on, email{' '}
              <a href="mailto:help@getro.com" target="_blank" rel="noopener noreferrer">
                help@getro.com
              </a>
              .
            </Text>
          </BlankMessage>
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'In Setup') {
      return (
        <Flex flexDirection={['column']} alignItems="center" textAlign="center">
          <Image src={SearchIcon} width="64px" pb="4px" />
          <BlankMessage title="We are connecting the company jobs to your network" pt="4px">
            <Text>
              It usually takes 2 business days, we will email you when it is done.{' '}
              <Link
                color="inherit"
                href="https://help.getro.com/support/solutions/articles/65000179707-company-job-sources"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </Link>
              .
            </Text>
            <Text>
              You can also suggest a source{' '}
              <Link color="inherit" href={jobSourceChangeUrl} target="_blank" rel="noopener noreferrer">
                here
              </Link>
              .
            </Text>
          </BlankMessage>
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'In Review') {
      return (
        <Flex flexDirection={['column']} alignItems="center" textAlign="center">
          {lastSuccessfullyScrapedAt && (
            <PanelRow title="Last updated" mb="12px" textAlign="left">
              <Tooltip
                content={moment(lastSuccessfullyScrapedAt).format('D MMM, YYYY [at] hh:mm a')}
                trigger={<span>{moment().from(lastSuccessfullyScrapedAt, true)} ago</span>}
              />
            </PanelRow>
          )}
          {activeJobsCount > 0 && (
            <PanelRow title="Jobs" textAlign="left">
              <Text>{activeJobsCount} Total</Text>
            </PanelRow>
          )}
          <Image src={SearchIcon} width="64px" pt="8px" pb="4px" />
          <BlankMessage title="Our job source experts are reviewing the job source(s) for this company." pt="4px">
            <Text>
              Job extraction should be back in action within 2 business days. You can suggest a source,{' '}
              <Link color="inherit" href={jobSourceChangeUrl} target="_blank" rel="noopener noreferrer">
                here
              </Link>
              .
            </Text>
          </BlankMessage>
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'Unavailable') {
      return (
        <Flex flexDirection="column" alignItems="center" textAlign="center">
          <Image src={SearchIcon} width="64px" pb="4px" />
          <BlankMessage title="We couldn't find a job source" pt="4px">
            <Text>
              We&apos;ll check again every month. Meanwhile you can{' '}
              <Link color="inherit" href={jobSourceChangeUrl} target="_blank" rel="noopener noreferrer">
                suggest a source
              </Link>{' '}
              or{' '}
              <Link
                color="inherit"
                as={NavLink}
                to={`/networks/${slug}/companies/${companySlug}/post-job`}
                rel="noopener noreferrer"
              >
                post jobs manually
              </Link>
              .
            </Text>
            <Text>
              Or learn more about job sources{' '}
              <Link
                color="inherit"
                href="https://help.getro.com/support/solutions/articles/65000179707-company-job-sources"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </Link>
              .
            </Text>
          </BlankMessage>
        </Flex>
      );
    }

    if (jobSourceCustomerFacingStatus === 'Live') {
      return (
        <>
          <Text mb="8px" fontSize="14px">
            We&apos;re currently extracting jobs from {ocps.length === 1 ? 'this job source' : 'these job sources'}. You
            can report a change{' '}
            <Link color="inherit" href={jobSourceChangeUrl} target="_blank" rel="noopener noreferrer">
              here
            </Link>
            .
          </Text>
          {ocps.map((ocp, index) => (
            <React.Fragment key={ocp.id}>
              <JobSource source={ocp} currentUser={currentUser} company={company} index={index} />
            </React.Fragment>
          ))}
        </>
      );
    }

    return null;
  };

  return <Box title="Job extraction">{renderContent()}</Box>;
};

CareersPages.propTypes = {
  company: OrganizationSchema.isRequired,
  activeNetwork: NetworkSchema.isRequired,
  currentUser: UserSchema.isRequired,
};

export default CareersPages;
